import { User } from "@phosphor-icons/react";

/**
 * ActivityAvatar — Coolman Design System platform component
 * (EWO-ORD-005, PO 2026-02-04 · refactored to presentation-only 2026-02-04).
 *
 * PRESENTATION ONLY. This component knows nothing about Orders, Employees,
 * Prices, Inventory or any other domain record. It renders a circular
 * initial-badge whose meaning is fully described by the `activity` prop.
 *
 * Each consuming module owns its own derivation helper and passes the
 * resulting activity object in:
 *
 *   Orders     → lib/orders/activity.js       → getLastActivity(order)
 *   Workforce  → lib/workforce/activity.js    → getLastActivity(employee)
 *   Pricing    → lib/pricing/activity.js      → getLastActivity(sheet)
 *   Inventory  → lib/inventory/activity.js    → getLastActivity(reading)
 *   Fleet      → lib/fleet/activity.js        → getLastActivity(vehicle)
 *   Customers  → lib/customers/activity.js    → getLastActivity(customer)
 *   Tasks      → lib/tasks/activity.js        → getLastActivity(task)
 *
 * Contract:
 *   getLastActivity(record) → { action, name, at }
 *
 * Usage:
 *   import ActivityAvatar from "@/components/ActivityAvatar";
 *   import { getLastActivity } from "@/lib/orders/activity";
 *   <ActivityAvatar activity={getLastActivity(order)} testId={order.order_number} />
 *
 * Action colours (PO-defined; extend ACTION_COLORS to introduce more —
 * the component itself never needs to change):
 *   Created → blue · Sent → green · Edited → orange · Re-Sent → purple ·
 *   Cancelled → red · Completed / Archived → gray.
 */

export const ACTION_COLORS = {
  Created:   { bg: "bg-blue-500",    ring: "ring-white" },
  Sent:      { bg: "bg-emerald-500", ring: "ring-white" },
  Edited:    { bg: "bg-orange-500",  ring: "ring-white" },
  "Re-Sent": { bg: "bg-violet-600",  ring: "ring-white" },
  Cancelled: { bg: "bg-red-600",     ring: "ring-white" },
  Completed: { bg: "bg-slate-800",   ring: "ring-white" },
  Archived:  { bg: "bg-slate-800",   ring: "ring-white" },
};

const DEFAULT_TONE = { bg: "bg-slate-400", ring: "ring-white" };

export function initialsFrom(name) {
  return String(name || "")
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase() || "?";
}

/**
 * Header cell contents for a data-table column that uses ActivityAvatar
 * in its rows. PO EWO-ORD-005: replace the text "Activity" with a
 * Person icon (no label required).
 */
export function ActivityColumnHeader({ testId = "activity-col-header" }) {
  return (
    <span
      data-testid={testId}
      className="inline-flex h-6 w-6 items-center justify-center text-slate-500"
      title="Most recent workflow action"
      aria-label="Activity"
    >
      <User size={16} weight="regular" />
    </span>
  );
}

/**
 * @param {object} activity          — { action, name, at }
 *   action ∈ Created | Sent | Edited | Re-Sent | Cancelled | Completed | Archived
 *   name    — full name of the responsible employee
 *   at      — ISO timestamp OR Date object
 * @param {string} testId            — data-testid suffix (unique per row)
 * @param {number} size              — px, default 30
 */
export default function ActivityAvatar({ activity, testId, size = 30 }) {
  const { action = "Created", name = "—", at } = activity || {};
  const tone = ACTION_COLORS[action] || DEFAULT_TONE;
  const initials = initialsFrom(name);

  const d = at ? new Date(at) : null;
  const isValid = d && !Number.isNaN(d.getTime());
  const now = new Date();
  const isToday = isValid && d.toDateString() === now.toDateString();
  const dateStr = !isValid
    ? ""
    : isToday
      ? "Today"
      : d.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  const timeStr = !isValid ? "" : d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

  return (
    <div
      className="group relative inline-flex"
      data-testid={testId ? `activity-avatar-${testId}` : "activity-avatar"}
    >
      {/* Avatar — always corporate blue with white initials. Identifies WHO. */}
      <div
        className="flex items-center justify-center rounded-full bg-blue-600 font-semibold text-white shadow-sm"
        style={{ width: size, height: size, fontSize: Math.floor(size * 0.4) }}
        aria-label={`${action} by ${name}`}
      >
        {initials}
      </div>
      {/* Status Badge — small coloured dot on the upper-right edge of the
          avatar. Identifies WHAT the latest action was. PO EWO-ORD-005+ */}
      <span
        data-testid={testId ? `activity-status-badge-${testId}` : "activity-status-badge"}
        aria-label={action}
        className={`absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full ring-2 ${tone.bg} ${tone.ring}`}
      />
      <div
        role="tooltip"
        data-testid={testId ? `activity-tooltip-${testId}` : "activity-tooltip"}
        className="pointer-events-none absolute left-1/2 top-full z-30 mt-2 w-52 -translate-x-1/2 rounded-md border border-gray-200 bg-white p-3 text-left text-xs text-gray-700 opacity-0 shadow-lg transition-opacity group-hover:opacity-100"
      >
        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500">Last Activity</div>
        <div className="font-semibold text-gray-900">{action}</div>
        <div className="text-gray-800">{name}</div>
        {dateStr && <div className="mt-1 text-gray-500">{dateStr}</div>}
        {timeStr && <div className="text-gray-500">{timeStr}</div>}
      </div>
    </div>
  );
}

// Domain-specific `getLastActivity(record)` helpers now live in
// `/app/frontend/src/lib/<module>/activity.js`. See the JSDoc above.
